import dayjs from 'dayjs';
import apiClient from '../../../requests/api/apiClient';
import { ChartFiltersDouble } from '../types';
import {
  buildCategoriesFromDates,
} from '../utils';

const getBarOffset = () => 86_400_000 / 3;
const HALF_DAY = 86_400_000 / 2;

const seriesOffset = (date: string, key: string) => {
  const base = dayjs(date).valueOf() + HALF_DAY;
  const offset = getBarOffset();
  if (key === 'et0') return base - (offset / 3);
  if (key === 'irrigation') return base - offset;
  if (key === 'rain') return base + (offset / 1.5);
  return base;
};

const normalizeSeries = (
  data: Array<{ date: string; [key: string]: number | string }>,
  key: string,
  type: 'et0' | 'irrigation' | 'rain' | 'kc' | 'cumulativeEt0',
) => {
  const series = data.map((item) => ({
    x: seriesOffset(item.date, type),
    y: item[key],
    custom: {
      date: dayjs(item.date).format('YYYY-MM-DD'),
    },
  }));

  return series;
};

const prepareData = async (filters: ChartFiltersDouble, telemetryType?: string) => {
  const granularity = 'daily';

  const absoluteCategories = buildCategoriesFromDates(filters.startDate, filters.endDate);

  const {
    data: {
      kcData,
      accumulatedEt0ForKc,
      normalizedIrrigation,
      normalizedEt0,
      rain,
    },
  } = await apiClient.readings.getIrrigationChartData({
    farmId: filters.farmId,
    startDate: filters.startDate,
    endDate: filters.endDate,
    sectorId: filters.sectorId || '',
    wiseconnZoneId: filters.wiseconnZoneId || '',
    weatherZoneId: filters.weatherZoneId || '',
    telemetryType: telemetryType || filters.telemetryType,
    sn: telemetryType === 'sn' ? filters.sn : undefined,
    chart: filters.chart,
  });

  const cumulativeEt0 = normalizeSeries(accumulatedEt0ForKc, 'et0', 'cumulativeEt0');
  const et0ChartData = normalizeSeries(normalizedEt0, 'et0', 'et0');
  const irrigationChartData = normalizeSeries(normalizedIrrigation, 'cumulativeTotal', 'irrigation');
  const rainChartData = normalizeSeries(rain, 'cumulativeTotal', 'rain');

  const kcRecord: Record<string, any> = {};
  const irrigationRecord: Record<string, any> = {};
  const et0Record: Record<string, any> = {};
  const rainRecord: Record<string, any> = {};
  const cumulativeEt0Record: Record<string, any> = {};

  kcData.forEach((item: any) => {
    kcRecord[item.date] = item.kc;
  });

  normalizedIrrigation.forEach((item: any) => {
    irrigationRecord[item.date] = item.cumulativeTotal;
  });

  normalizedEt0.forEach((item: any) => {
    et0Record[item.date] = item.et0;
  });

  rain.forEach((item: any) => {
    rainRecord[item.date] = item.cumulativeTotal;
  });

  accumulatedEt0ForKc.forEach((item: any) => {
    cumulativeEt0Record[item.date] = item.et0;
  });

  return {
    et0: et0ChartData,
    cumulativeEt0,
    rain: rainChartData,
    irrigation: irrigationChartData,
    kcRecord,
    irrigationRecord,
    et0Record,
    rainRecord,
    cumulativeEt0Record,
    absoluteCategories,
    granularity,
  };
};

export default prepareData;
